import { Injectable } from '@angular/core';
import { Environment } from '../classes/environment.context.class';
import { Context } from '../classes/context.class';
import { LocalStorageUtilWrapperService } from 'src/app/services/local-storage-util-wrapper.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ContextWidgetStorageService {
  private CONTEXT_WIDGET_DATA_KEY = '$CONTEXT_WIDGET_DATA_KEY$';

  persistentContextStorage: Map<string, Context> = new Map<string, Context>();

  storageEnvironmentChanged: Subject<any> = new Subject<any>();

  fetchedForProject: Set<string> = new Set<string>();

  //This is a subject used to notify context-switcher to refresh its data
  refreshEnvironmentData: Subject<any> = new Subject<any>();

  constructor(private localStorageService: LocalStorageUtilWrapperService) {
    this.initializePersistentStorage();
  }

  initializePersistentStorage() {
    this.persistentContextStorage =
      this.fetchPersistentContextStorageFromLocalStorage();
  }

  fetchPersistentContextStorageFromLocalStorage(): Map<string, Context> {
    const storedData = this.localStorageService.get(
      this.CONTEXT_WIDGET_DATA_KEY,
    );

    if (!storedData) {
      return new Map<string, Context>();
    }

    const contextMap = new Map<string, Context>();
    Object.keys(storedData).forEach((projectName) => {
      const context = storedData[projectName] as Context;
      contextMap.set(projectName, context);
    });

    return contextMap;
  }

  projectEnvironmentMap: Map<string, Environment[]> = new Map<
    string,
    Environment[]
  >();

  hasFetchedForPreviously(project: string) {
    return this.fetchedForProject.has(project);
  }

  markAsFetchedOnce(project: string) {
    this.fetchedForProject.add(project);
  }

  //If `refreshContext` is `true`, it triggers a refresh for the context switcher.
  setEnvironmentsForProject(
    project: string,
    environments: Environment[],
    refreshContext: boolean = false,
  ) {
    this.projectEnvironmentMap.set(project, environments);

    if (refreshContext) {
      this.refreshEnvironmentData.next(project);
    }
  }

  getEnvironmentsForProject(project: string) {
    return this.projectEnvironmentMap.get(project) || [];
  }

  hasEnvironmentsForProject(project: string) {
    return this.projectEnvironmentMap.get(project)?.length > 0;
  }

  //Set 'refreshContext' to true when updating environment list in the same component where context-widget was initialized
  updateEnvironmentsForProject(
    action: 'add' | 'delete' | 'update' | 'replace',
    project: string,
    environment: Environment,
    refreshContext: boolean = false,
  ) {
    let allEnvironments = this.getEnvironmentsForProject(project);
    switch (action) {
      case 'add':
        allEnvironments.push({
          name: environment?.name,
          id: environment?.id,
          cloud: environment?.cloud,
          stackName: environment?.stackName,
          clusterState: environment?.clusterState,
        });
        this.setLastEnvironment(project, environment);
        break;
      case 'delete':
        if (this.getLastEnvironment(project)?.id === environment?.id) {
          this.removeLastEnvironment(project);
        }
        allEnvironments = allEnvironments?.filter(
          (env) => env?.id !== environment?.id,
        );
        break;
      case 'update':
        this.updateEnvironmentsForProject('delete', project, environment);
        this.updateEnvironmentsForProject('add', project, environment);
        allEnvironments = this.getEnvironmentsForProject(project);
        break;
    }
    this.setEnvironmentsForProject(project, allEnvironments, refreshContext);
  }

  hasContextStored(project: string) {
    return this.persistentContextStorage.has(project);
  }

  getLastEnvironment(project: string) {
    return this.persistentContextStorage?.get(project)?.environment;
  }

  setLastEnvironment(project: string, newEnvironment: Environment) {
    if (!this.hasContextStored(project)) {
      this.persistentContextStorage.set(project, {} as Context);
    }
    this.persistentContextStorage.get(project).environment = newEnvironment;
    this.updatePersistentStorage();
    this.storageEnvironmentChanged.next(newEnvironment);
  }

  removeLastEnvironment(project: string) {
    if (this.hasContextStored(project)) {
      this.persistentContextStorage.delete(project);
      this.updatePersistentStorage();
    }
  }

  updatePersistentStorage() {
    this.localStorageService.set(
      this.CONTEXT_WIDGET_DATA_KEY,
      this.serializeContextStorage(this.persistentContextStorage),
    );
  }

  serializeContextStorage(map: Map<string, Context>): {
    [key: string]: Context;
  } {
    const serializedValue: { [key: string]: Context } = {};

    map.forEach((contextValue, key) => {
      serializedValue[key] = { ...contextValue };
    });

    return serializedValue;
  }

  clearContextWidgetStorage() {
    this.persistentContextStorage?.clear();
    this.localStorageService.remove(this.CONTEXT_WIDGET_DATA_KEY);
  }
}
